import { useContext, useEffect, useState } from "react";
import { HomeContext } from "../containers/Home";
import { getCampaignTasks } from "../services/api-server/projects";
import { TPhaseDataWithTasks } from "../types/phase";
import { TProjectData } from "../types/project";
import { TTaskData } from "../types/task";
import Emitter from "../utils/emitter";
import DeliverableConfigurationModal from "./Modal/DeliverableConfigurationModal";
import TaskConfigurationModal from "./Modal/TaskConfigurationModal";

function TaskConfiguration({
	projectData,
	setOtherData,
}: {
	projectData: TProjectData;
	setOtherData: (data: any) => void;
}) {
	const [openModal, setOpenModal] = useState<any>(false);
	const [allCampaignTasks, setAllCampaignTasks] = useState<any>([]);
	const [phaseTaskRecord, setPhaseTaskRecord] = useState<
		(TPhaseDataWithTasks & { task: TTaskData }) | null
	>(null);
	const [deliverableModalOpen, setDeliverableModalOpen] = useState<any>(false);
	const [deliverableRecord, setDeliverableRecord] = useState<any>(null);

	const { currentTenantKey, userInfo, allData, handleFormUpdating }: any =
		useContext(HomeContext);

	useEffect(() => {
		Emitter.on(
			"taskModalOpen",
			async (payload: TPhaseDataWithTasks & { task: TTaskData }) => {
				try {
					setPhaseTaskRecord(payload);
					setOpenModal(true);
				} catch (error) {}
			}
		);
		return () => {
			Emitter.remove("taskModalOpen");
		};
	}, []);

	useEffect(() => {
		// Find all tasks related to the current project
		const fetchCampaignTasks = async () => {
			if (projectData?._id) {
				const campaignTasks = await getCampaignTasks(
					currentTenantKey,
					projectData._id
				);
				setAllCampaignTasks(campaignTasks);
			}
		};

		fetchCampaignTasks();
	}, [projectData, currentTenantKey, location.pathname, allData]);

	const handleTaskFormUpdate = (payload: any) => {
		setOtherData({
			project_code: projectData?.project_code,
			_id: projectData._id,
		});
		if (payload) {
			setPhaseTaskRecord({
				...phaseTaskRecord,
				task: payload,
			} as TPhaseDataWithTasks & { task: TTaskData });
		}
	};

	const handleTaskModalClose = () => {
		setPhaseTaskRecord(null);
	};

	const getRoleAssignments = () => {
		const roles = [...(projectData?.role_assignments || [])];

		if (projectData?.project_manager) {
			roles.push({
				role: "Project manager",
				role_id: "project_manager",
				appointee: [...projectData.project_manager],
			});
		}

		return roles;
	};

	// Deliverable functions
	const handleDeliverableFormUpdate = (payload: any = null) => {
		setOtherData({
			project_code: projectData?.project_code,
			_id: projectData._id,
		});
		setPhaseTaskRecord({
			...phaseTaskRecord,
			task: { ...phaseTaskRecord?.task },
		} as TPhaseDataWithTasks & { task: TTaskData });
		setDeliverableRecord(payload);
	};

	const handleDeliverableModalClose = () => {
		handleFormUpdating(false, deliverableRecord?._id || "deliverable_new");
		setDeliverableModalOpen(false);
		setDeliverableRecord(null);
	};

	return (
		<>
			{phaseTaskRecord?._id && allCampaignTasks && (
				<TaskConfigurationModal
					open={openModal}
					setOpen={setOpenModal}
					option={
						projectData?.project_scope === "multi-asset" ? "multi-asset" : null
					}
					handleFormUpdate={handleTaskFormUpdate}
					handleModalClose={handleTaskModalClose}
					setDeliverableModalOpen={setDeliverableModalOpen}
					setDeliverableRecord={setDeliverableRecord}
					currentProject={projectData}
					record={phaseTaskRecord}
					allCampaignTasks={allCampaignTasks}
					phaseID={phaseTaskRecord?._id}
					taskRecord={phaseTaskRecord?.task}
					activeRowKey={phaseTaskRecord?.task?._id}
					extraData={{
						...allData,
						role_assignments: getRoleAssignments(),
					}}
					user={userInfo?.user}
					userRole={userInfo?.userRole}
				/>
			)}
			<DeliverableConfigurationModal
				open={deliverableModalOpen}
				setOpen={setDeliverableModalOpen}
				handleFormUpdate={handleDeliverableFormUpdate}
				handleModalClose={handleDeliverableModalClose}
				taskID={phaseTaskRecord?.task?._id}
				deliverableRecord={deliverableRecord}
				activeRowKey={deliverableRecord?._id}
				extraData={{
					role_assignments: getRoleAssignments(),
				}}
				currentProject={projectData}
				user={userInfo?.user}
				userRole={userInfo?.userRole}
				taskRecord={phaseTaskRecord?.task}
			/>
		</>
	);
}

export default TaskConfiguration;
